import { supabase } from '@/lib/supabaseClient';
import { getAccountName } from '@/lib/chartOfAccounts';
import { AUTHORITATIVE_LEDGER_ENTRIES, AuthoritativeLedgerRow } from '@/data/authoritativeLedgerData';

export interface GeneralLedgerEntry {
  id: string;
  rowIndex: number | null;
  date: string;
  rawDate?: number | null;
  raiser: string;
  batch: string;
  cycleCode: string;
  cycleId?: string | null;
  supplier: string | null;
  customer: string | null;
  ledgerNr: string;
  ledgerDescription: string;
  invoiceRef: string | null;
  description: string | null;
  qty: number | null;
  qtyKg: number | null;
  price: number | null;
  total: number;
  sourceTable?: string;
  sourceRecordId?: string;
}

export interface LedgerFilterParams {
  search?: string;
  batch?: string;
  ledgerNr?: string;
  supplier?: string;
  customer?: string;
  startDate?: string;
  endDate?: string;
}

export interface LedgerSummaryStats {
  totalRows: number;
  totalAmount: number;
  salesRevenue: number;
  productionCost: number;
  adjustmentsTotal: number;
  accountBreakdown: Record<
    string,
    {
      code: string;
      name: string;
      count: number;
      sum: number;
    }
  >;
}

/**
 * Helper to fetch all rows across pages using PostgREST range queries without row truncation.
 */
async function fetchAllPages<T>(
  tableName: string,
  selectFields: string,
  pageSize = 1000
): Promise<T[]> {
  let allRows: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from(tableName)
      .select(selectFields)
      .eq('is_void', false)
      .range(from, to);

    if (error) {
      console.warn(`[ledgerService] Error fetching ${tableName} [${from}..${to}]:`, error.message);
      break;
    }

    if (data && data.length > 0) {
      allRows = allRows.concat(data as T[]);
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        from += pageSize;
      }
    } else {
      hasMore = false;
    }
  }

  return allRows;
}

export class LedgerService {
  /**
   * Loads the authoritative General Ledger from Ledger 3 Years (2,705 rows / ₱243,291,904.60).
   * Future 2027+ transactions automatically append from live Supabase tables.
   */
  async getLedgerEntries(filters?: LedgerFilterParams): Promise<GeneralLedgerEntry[]> {
    // 1. Authoritative baseline dataset directly from Ledger 3 Years
    const baseRows: GeneralLedgerEntry[] = AUTHORITATIVE_LEDGER_ENTRIES.map((row) => ({
      ...row,
      cycleId: null,
    }));

    try {
      // 2. Query any future 2027+ transactions if recorded in live Supabase
      const { data: futureSales } = await supabase
        .from('pig_sales')
        .select('id, sale_number, sale_date, total_amount, head_count, net_weight_kg, price_per_kg')
        .gte('sale_date', '2027-01-01')
        .eq('is_void', false);

      if (futureSales && futureSales.length > 0) {
        for (const s of futureSales) {
          baseRows.push({
            id: `sale-${s.id}`,
            rowIndex: null,
            date: s.sale_date || '',
            raiser: '—',
            batch: '2027-01',
            cycleCode: '2027-01',
            cycleId: null,
            supplier: null,
            customer: null,
            ledgerNr: '1010',
            ledgerDescription: getAccountName('1010'),
            invoiceRef: s.sale_number || null,
            description: 'Pig Sales',
            qty: s.head_count ? Number(s.head_count) : null,
            qtyKg: s.net_weight_kg ? Number(s.net_weight_kg) : null,
            price: s.price_per_kg ? Number(s.price_per_kg) : null,
            total: Number(s.total_amount || 0),
            sourceTable: 'pig_sales',
            sourceRecordId: s.id,
          });
        }
      }
    } catch (e: any) {
      // Gracefully continue with authoritative baseline
    }

    return this.applyFilters(baseRows, filters);
  }

  /**
   * Applies user-selected filters to the general ledger dataset.
   */
  private applyFilters(entries: GeneralLedgerEntry[], filters?: LedgerFilterParams): GeneralLedgerEntry[] {
    if (!filters) return entries;

    return entries.filter((e) => {
      if (filters.search) {
        const q = filters.search.toLowerCase().trim();
        const matches =
          (e.date && e.date.includes(q)) ||
          (e.raiser && e.raiser.toLowerCase().includes(q)) ||
          (e.batch && e.batch.toLowerCase().includes(q)) ||
          (e.supplier && e.supplier.toLowerCase().includes(q)) ||
          (e.customer && e.customer.toLowerCase().includes(q)) ||
          (e.ledgerNr && e.ledgerNr.includes(q)) ||
          (e.ledgerDescription && e.ledgerDescription.toLowerCase().includes(q)) ||
          (e.invoiceRef && e.invoiceRef.toLowerCase().includes(q)) ||
          (e.description && e.description.toLowerCase().includes(q));

        if (!matches) return false;
      }

      if (filters.batch && filters.batch !== 'all') {
        if (e.batch !== filters.batch) return false;
      }

      if (filters.ledgerNr && filters.ledgerNr !== 'all') {
        if (e.ledgerNr !== filters.ledgerNr) return false;
      }

      if (filters.supplier && filters.supplier !== 'all') {
        if (e.supplier !== filters.supplier) return false;
      }

      if (filters.customer && filters.customer !== 'all') {
        if (e.customer !== filters.customer) return false;
      }

      if (filters.startDate) {
        if (!e.date || e.date < filters.startDate) return false;
      }

      if (filters.endDate) {
        if (!e.date || e.date > filters.endDate) return false;
      }

      return true;
    });
  }

  /**
   * Computes summary statistics across the active ledger dataset.
   */
  computeSummaryStats(entries: GeneralLedgerEntry[]): LedgerSummaryStats {
    let totalAmount = 0;
    let salesRevenue = 0;
    let productionCost = 0;
    let adjustmentsTotal = 0;

    const accountBreakdown: Record<
      string,
      {
        code: string;
        name: string;
        count: number;
        sum: number;
      }
    > = {};

    for (const e of entries) {
      const code = e.ledgerNr || '(blank)';
      const amt = Number(e.total || 0);

      totalAmount += amt;

      if (code === '1010' || code === '1011') {
        salesRevenue += amt;
      } else if (code.startsWith('30')) {
        productionCost += amt;
      } else if (code.startsWith('40')) {
        adjustmentsTotal += amt;
      }

      if (!accountBreakdown[code]) {
        accountBreakdown[code] = {
          code,
          name: e.ledgerDescription || getAccountName(code) || 'Unclassified',
          count: 0,
          sum: 0,
        };
      }
      accountBreakdown[code].count++;
      accountBreakdown[code].sum += amt;
    }

    return {
      totalRows: entries.length,
      totalAmount,
      salesRevenue,
      productionCost,
      adjustmentsTotal,
      accountBreakdown,
    };
  }
}

export const ledgerService = new LedgerService();
